import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "sonner";
import { getDomesticHelperQuote } from "../../../../api/domesticHelperServices";
import useDomesticHelperFlow from "../../../../hooks/useDomesticHelperFlowSimple";
import useDomesticHelperFormData from "../../../../hooks/useDomesticHelperFormData";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import {
  selectDomesticHelperQuoteData,
  selectDomesticHelperQuoteLoading,
  setQuoteData,
  setQuoteError,
  setQuoteLoading,
} from "../../../../redux/features/domesticHelperFormSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import { selectHelperDetails } from "../../../../redux/features/domesticHelperFlowSlice";
import {
  fetchPartyCode,
  selectPartyCode,
} from "../../../../redux/features/quoteSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";
import { formatPremiumWithCurrency } from "../../../../utils/index";
import FileUpload from "../../../common/fileUploadInput/fileUploadInput";
import Input from "../../../common/Input/Input";
import SelectInput from "../../../common/selectInput/selectInput";
import SingleDatePicker from "../../../common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../common/tabs/formTabs";
import DomesticHelperSummaryModal from "../../../newDomesticHelperPolicy/modals/domesticHelperSummaryModal";
import TitleDescription from "../../../newMotorPolicy/titleDescription/titleDescription";
import DomesticHelperBottomStickyBtnLayout from "../../layouts/DomesticHelperBottomStickyBtnLayout";
import DomesticHelperJourneyLayout from "../../layouts/DomesticHelperJourneyLayout";
import "./aboutYourHelper.css";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "male" },
  { label_en: "Female", label_ar: "أنثى", type: "female" },
];

export const ABOUT_YOUR_HELPER_FORM = "ABOUT_YOUR_HELPER_FORM";

/** How long the form must sit still before a changed answer is re-priced. */
const QUOTE_DEBOUNCE_MS = 800;

/**
 * Every input the quote is priced from, as one comparable string.
 *
 * This replaces a single "already called the API" boolean. A boolean can only
 * say that a quote was fetched at some point — never whether it still matches
 * what is on the form. Once it was set, editing the nationality (or the name,
 * passport number, date of birth or gender) left the first quote's premium on
 * screen and carried its quote number all the way through to payment. Only CPR
 * had an escape hatch, because it had been given a reset of its own.
 *
 * Listed field by field rather than stringifying the whole form, so state that
 * has nothing to do with pricing — passport upload progress, the raw scan
 * payload — cannot trigger a pointless re-quote. Kept in step with what
 * buildQuotePayload in domesticHelperServices actually reads.
 */
const buildQuoteSignature = (helper, application, partyCode) =>
  JSON.stringify([
    helper?.fullName?.trim() || "",
    helper?.passportNumber?.trim() || "",
    (helper?.cprNumber || "").replace(/\D/g, ""),
    helper?.nationality ?? "",
    helper?.dateOfBirth?.day || "",
    helper?.dateOfBirth?.month || "",
    helper?.dateOfBirth?.year || "",
    helper?.gender || "",
    application?.applicationType || "",
    application?.employmentType || "",
    application?.limitForAbsence || "",
    application?.policyPeriod || "",
    partyCode || "FALCON",
  ]);

const AboutYourHelper = () => {
  const translate = useLanguageTranslate();
  const { i18n } = useTranslation();
  const dispatch = useDispatch();
  const { handleNextStep, handlePrevSubstep } = useDomesticHelperFlow();

  // Always use English locale for numbers
  const isRTL = i18n.resolvedLanguage === "ar";

  // Get quote data from Redux
  const quoteData = useSelector(selectDomesticHelperQuoteData);
  const isLoadingQuote = useSelector(selectDomesticHelperQuoteLoading);

  // Get login info and party code from Redux
  const loginTokenInfo = useSelector(selectLoginTokenInfo);
  const partyCode = useSelector(selectPartyCode);
  const isLoggedIn = !!loginTokenInfo;

  // Get application details from Redux for premium calculation context
  const applicationDetailsData =
    useSelector(
      (state) =>
        state.domesticHelperForm?.DOMESTIC_HELPER_APPLICATION_DETAILS_FORM
    ) || {};

  // Get proposer (customer) basic info captured on the start page so we can
  // pass it through to the quote payload (otherwise it's orphaned).
  const proposerBasicInfo =
    useSelector(selectHelperDetails)?.basicInfo || null;

  // Get nationality data from API via Redux
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Initialize form data state
  const [formData, setFormData] = useState({
    fullName: "",
    passportNumber: "",
    cprNumber: "",
    domesticApplicationNumber: "",
    nationality: "", // No default value
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
    },
    gender: "", // No default value
    // Passport upload state
    passportUploaded: false,
    passportProcessing: false,
    passportData: null,
    passportProcessed: false,
  });

  const [errors, setErrors] = useState({});
  const [showSummaryModal, setShowSummaryModal] = useState(false);
  // The signature the quote currently in Redux was priced from. State, not a
  // ref, because the render needs it: a quote that no longer matches the form
  // must not be shown as this helper's price, nor be carried forward by
  // Continue. Seeded from the stored quote so returning to this step with
  // nothing changed keeps the price it already has.
  const [quotedSignature, setQuotedSignature] = useState(
    () => quoteData?.signature ?? null
  );


  // Passport file data for FileUpload component persistence
  const [passportFile, setPassportFile] = useState(null);

  // Track which party code was used for the last quote to detect changes
  const lastQuotePartyCodeRef = useRef(null);

  const { saveDomesticHelperDataInRedux, getDomesticHelperFormData } =
    useDomesticHelperFormData(ABOUT_YOUR_HELPER_FORM);


  // Fetch nationalities on component mount
  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Fetch party code for logged-in users
  useEffect(() => {
    if (isLoggedIn && !partyCode && loginTokenInfo?.cpr) {
      console.log(
        "Domestic Helper - Fetching party code for logged-in user:",
        loginTokenInfo.cpr
      );
      dispatch(fetchPartyCode(loginTokenInfo.cpr));
    }
  }, [isLoggedIn, partyCode, loginTokenInfo?.cpr, dispatch]);

  // Load saved form data on component mount
  useEffect(() => {
    const savedFormData = getDomesticHelperFormData();
    if (savedFormData) {
      setFormData({
        fullName: savedFormData.fullName || "",
        passportNumber: savedFormData.passportNumber || "",
        cprNumber: savedFormData.cprNumber || "",
        domesticApplicationNumber:
          savedFormData.domesticApplicationNumber || "",
        nationality: savedFormData.nationality || "",
        dateOfBirth: savedFormData.dateOfBirth || {
          day: "",
          month: "",
          year: "",
        },
        gender: savedFormData.gender || "",
        // Restore passport upload state
        passportUploaded: savedFormData.passportUploaded || false,
        passportProcessing: false, // Always reset processing state on mount
        passportData: savedFormData.passportData || null,
        passportProcessed: savedFormData.passportProcessed || false,
        passportFile: savedFormData.passportFile || null,
      });

      // Restore passport file state for FileUpload component
      if (savedFormData.passportFile) {
        setPassportFile(savedFormData.passportFile);
      }
    }
  }, []); // Remove dependency to prevent infinite loop on navigation back

  // Signing in mid-flow swaps FALCON for the customer's own party code. That
  // is part of the signature, so the re-price happens on its own — this only
  // announces it. Previously this effect had to reset the "already called"
  // flag by hand, which is why signing in was the one input change that did
  // re-quote.
  useEffect(() => {
    const currentPartyCode = partyCode || "FALCON";
    const lastPartyCode = lastQuotePartyCodeRef.current;

    if (!quoteData?.data) return;
    if (!lastPartyCode || currentPartyCode === lastPartyCode) return;

    toast.info(
      translate(
        "Updating quote with your account information...",
        "جاري تحديث التسعيرة بمعلومات حسابك..."
      )
    );
  }, [partyCode, quoteData, translate]);

  // Helper function to validate all fields without setting errors state
  const validateAllFields = (data) => {
    // Full name validation
    if (!data.fullName?.trim()) return false;
    if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(data.fullName)) return false;
    if (data.fullName.length < 2) return false;

    // Passport number validation
    if (!data.passportNumber?.trim()) return false;
    if (data.passportNumber.length < 6) return false;

    // Nationality validation
    if (!data.nationality) return false;
    const isBahraini =
      data.nationality === 14 ||
      (typeof data.nationality === "string" &&
        (data.nationality.toLowerCase().includes("bahrain") ||
          data.nationality.toLowerCase().includes("بحرين")));
    if (isBahraini) return false;

    // Date of birth validation
    if (!data.dateOfBirth.day || !data.dateOfBirth.month || !data.dateOfBirth.year) return false;
    const dateError = validateDateOfBirth(data.dateOfBirth);
    if (dateError) return false;

    // Gender validation
    if (!data.gender) return false;

    // CPR validation - must be 9 digits
    if (!data.cprNumber?.trim()) return false;
    if (!/^\d{9}$/.test(data.cprNumber.replace(/\D/g, ""))) return false;

    // Passport upload validation
    if (!data.passportUploaded) return false;

    return true;
  };

  // What the form would be priced at right now, and whether the quote on
  // screen still answers for it.
  const quoteSignature = buildQuoteSignature(
    formData,
    applicationDetailsData,
    partyCode
  );
  const isFormComplete = validateAllFields(formData);
  // Only meaningful once the form is complete — a half-cleared field is not a
  // stale quote, it is an unfinished one, and treating it as stale would leave
  // the price shimmering forever with nothing able to resolve it.
  const isQuoteStale = isFormComplete && quotedSignature !== quoteSignature;

  // Price the form whenever it settles on a combination that has not been
  // priced yet — first completion, and every edit after it.
  useEffect(() => {
    if (isLoadingQuote || formData.passportProcessing) return;
    // For logged-in users, wait for the party code to be fetched.
    if (isLoggedIn && !partyCode) return;
    if (!isFormComplete) {
      if (formData.cprNumber && formData.cprNumber.replace(/\D/g, "").length < 9) {
        console.log("⏳ Waiting for valid CPR (9 digits) before calling quote API");
      }
      return;
    }
    if (!isQuoteStale) return;

    // Debounced, because this now fires on every edit rather than once: without
    // it, typing into the name or passport field would put a request on the
    // wire per keystroke.
    const timer = setTimeout(() => {
      console.log("✅ Form complete and not yet priced - calling quote API");
      handleGetQuote(quoteSignature);
    }, QUOTE_DEBOUNCE_MS);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    quoteSignature,
    isQuoteStale,
    isFormComplete,
    isLoadingQuote,
    formData.passportProcessing,
    partyCode,
    isLoggedIn,
  ]);

  // Handle get quote API call
  const handleGetQuote = async (signature) => {
    try {
      dispatch(setQuoteLoading(true));
      // Claim the signature before awaiting, so the effect does not queue a
      // second request for the same answers while this one is in flight.
      setQuotedSignature(signature);

      // Use party code from Redux state (already fetched in useEffect)
      const effectivePartyCode = partyCode || "FALCON";

      console.log("=== Calling Domestic Helper Quote API ===");
      console.log("Using party code:", effectivePartyCode);

      // Store the party code we're using for this quote
      lastQuotePartyCodeRef.current = effectivePartyCode;
      console.log("📝 Saved party code for this quote:", effectivePartyCode);

      const response = await getDomesticHelperQuote(
        formData,
        applicationDetailsData,
        effectivePartyCode,
        { timeout: 30000 },
        proposerBasicInfo
      );

      console.log("API Quote Success:", response);
      // The signature rides along with the quote so coming back to this step
      // can tell an unchanged form (reuse the price) from an edited one
      // (re-price), instead of re-requesting on every back-navigation.
      dispatch(setQuoteData({ ...response, signature }));
    } catch (error) {
      console.error("Quote API Error:", error);
      dispatch(setQuoteError(error.message));
      setQuotedSignature(null); // Nothing is priced — allow a retry

      toast.error(
        translate(
          "Failed to calculate quote. Please try again.",
          "فشل في حساب السعر. يرجى المحاولة مرة أخرى."
        )
      );
    }
  };

  // Validate individual field for immediate error display
  const validateField = (field, value, currentFormData = formData) => {
    const newErrors = { ...errors };

    if (field === "fullName") {
      if (!value?.trim()) {
        newErrors.fullName = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(value)) {
        newErrors.fullName = translate(
          "Name can only contain alphabetic characters, spaces, and periods.",
          "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
        );
      } else if (value.length < 2) {
        newErrors.fullName = translate(
          "Name must be at least 2 characters long.",
          "يجب أن يكون الاسم مكونًا من حرفين على الأقل."
        );
      } else {
        delete newErrors.fullName;
      }
    } else if (field === "passportNumber") {
      if (!value?.trim()) {
        newErrors.passportNumber = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else if (value.length < 6) {
        newErrors.passportNumber = translate(
          "Passport number must be at least 6 characters long.",
          "يجب أن يكون رقم جواز السفر مكونًا من 6 أحرف على الأقل."
        );
      } else {
        delete newErrors.passportNumber;
      }
    } else if (field === "cprNumber") {
      if (!value || !value.trim()) {
        newErrors.cprNumber = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else if (!/^\d{9}$/.test(value.replace(/\D/g, ""))) {
        newErrors.cprNumber = translate(
          "CPR must be 9 digits.",
          "يجب أن يتكوّن رقم بطاقة الهوية من 9 أرقام."
        );
      } else {
        delete newErrors.cprNumber;
      }
    } else if (field === "nationality") {
      if (!value) {
        newErrors.nationality = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else {
        // Check if nationality is Bahraini (code 14 or string containing "bahrain")
        const isBahraini =
          value === 14 ||
          (typeof value === "string" &&
            (value.toLowerCase().includes("bahrain") ||
              value.toLowerCase().includes("بحرين")));

        if (isBahraini) {
          newErrors.nationality = translate(
            "Helper nationality cannot be Bahraini.",
            "جنسية العامل المنزلي لا يمكن أن تكون بحرينية."
          );
        } else {
          delete newErrors.nationality;
        }
      }
    } else if (field === "gender") {
      if (!value) {
        newErrors.gender = translate(
          "This field is required.",
          "هذا الحقل مطلوب."
        );
      } else {
        delete newErrors.gender;
      }
    }

    setErrors(newErrors);
  };

  const handleInputChange = (field, value) => {
    let processedValue = value;

    // Apply input formatting based on field type
    if (field === "fullName") {
      // Full Name: Allow alphabetic characters, spaces, and periods only
      processedValue = value;
    } else if (field === "passportNumber") {
      // Passport: Only allow alphanumeric characters
      processedValue = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
    } else if (field === "cprNumber") {
      // CPR: Only allow digits, max 9 characters
      processedValue = value.replace(/\D/g, "");
      if (processedValue.length > 9) {
        processedValue = processedValue.substring(0, 9);
      }
    } else if (field === "domesticApplicationNumber") {
      // Domestic Application Number: Only allow alphanumeric characters (preserve case)
      processedValue = value.replace(/[^a-zA-Z0-9]/g, "");
    }

    const updatedFormData = {
      ...formData,
      [field]: processedValue,
    };

    setFormData(updatedFormData);

    // Real-time validation when there's already an error for this field
    if (errors[field]) {
      validateField(field, processedValue, updatedFormData);
    }
  };

  // Separate function to validate date of birth
  // A function declaration, not a const arrow: validateAllFields calls this and
  // now runs during render, above this point in the file. A const would still
  // be in its temporal dead zone at that moment and throw.
  function validateDateOfBirth(dateOfBirth) {
    if (!dateOfBirth.day || !dateOfBirth.month || !dateOfBirth.year) {
      return null; // Not complete yet, no error
    }

    // Additional validation: Check if the date components are valid numbers
    const day = parseInt(dateOfBirth.day);
    const month = parseInt(dateOfBirth.month);
    const year = parseInt(dateOfBirth.year);

    // Validate day range (1-31)
    if (isNaN(day) || day < 1 || day > 31) {
      return translate(
        "Please enter a valid day (1-31).",
        "يرجى إدخال يوم صحيح (1-31)."
      );
    }
    // Validate month range (1-12)
    else if (isNaN(month) || month < 1 || month > 12) {
      return translate(
        "Please enter a valid month (1-12).",
        "يرجى إدخال شهر صحيح (1-12)."
      );
    }
    // Validate year range (reasonable birth years)
    else if (isNaN(year) || year < 1900 || year > new Date().getFullYear()) {
      return translate(
        "Please enter a valid birth year.",
        "يرجى إدخال سنة ميلاد صحيحة."
      );
    } else {
      // Create the birth date and validate it's a real date
      const birthDate = new Date(year, month - 1, day);

      // Check if the date is valid (JavaScript Date constructor can create invalid dates)
      if (
        birthDate.getDate() !== day ||
        birthDate.getMonth() !== month - 1 ||
        birthDate.getFullYear() !== year
      ) {
        return translate(
          "Please enter a valid date.",
          "يرجى إدخال تاريخ صحيح."
        );
      } else {
        // Date is valid, now check age and future date constraints
        const today = new Date();

        if (birthDate > today) {
          return translate(
            "Date of birth cannot be in the future.",
            "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
          );
        } else {
          // Calculate age
          let age = today.getFullYear() - birthDate.getFullYear();
          const monthDiff = today.getMonth() - birthDate.getMonth();

          if (
            monthDiff < 0 ||
            (monthDiff === 0 && today.getDate() < birthDate.getDate())
          ) {
            age--;
          }

          if (age < 18) {
            return translate(
              "Helper must be at least 18 years old.",
              "يجب أن يكون عمر العامل المنزلي 18 سنة على الأقل."
            );
          }
        }
      }
    }

    return null; // No error
  }

  const handleDateChange = (field, value) => {
    const updatedDateOfBirth = {
      ...formData.dateOfBirth,
      [field]: value,
    };

    const updatedFormData = {
      ...formData,
      dateOfBirth: updatedDateOfBirth,
    };

    setFormData(updatedFormData);

    // Validate immediately after date change
    const dateError = validateDateOfBirth(updatedDateOfBirth);
    setErrors((prev) => ({
      ...prev,
      dateOfBirth: dateError || "",
    }));
  };

  const handleGenderChange = (gender) => {
    const updatedFormData = {
      ...formData,
      gender,
    };

    setFormData(updatedFormData);

    // Validate immediately if there's already an error
    if (errors.gender) {
      validateField("gender", gender, updatedFormData);
    }
  };

  // Passport upload handlers for FileUpload component with OCR support
  const handlePassportUpload = async (
    uploaded,
    fileName,
    passportData = null,
    fileSize = null
  ) => {
    console.log(
      "Passport Upload status:",
      uploaded,
      "File:",
      fileName,
      "OCR Data:",
      passportData
    );

    if (uploaded) {
      // Create file data object for persistence
      const fileInfo = {
        fileName: fileName, // Actual file name with extension
        fileSize: fileSize || "Unknown", // Use provided file size or fallback
        file: { name: fileName },
        uploadedAt: new Date().toISOString(),
      };

      // Store file data for FileUpload component persistence
      setPassportFile(fileInfo);

      // Update form data with uploaded file
      setFormData((prev) => ({
        ...prev,
        passportUploaded: true,
        passportProcessing: false, // OCR already processed in FileUpload component
        passportData: passportData,
        passportProcessed: !!passportData,
        passportFile: fileInfo, // Store file info in form data for Redux persistence
      }));

      // Clear any errors when files are uploaded
      setErrors((prev) => ({ ...prev, passport: "" }));

      // Auto-fill form data if OCR was successful
      if (passportData) {
        setFormData((prev) => {
          // CRITICAL: Convert nationality string to numeric code
          let nationalityCode = prev.nationality; // Keep previous value by default

          if (passportData.nationality) {
            const nationalityString = passportData.nationality.toString().trim();

            // Check if it's Bahraini nationality
            const isBahraini =
              nationalityString === "14" ||
              nationalityString.toLowerCase().includes("bahrain") ||
              nationalityString.toLowerCase().includes("بحرين");

            // Try to find the nationality in options (works for both Bahraini and others)
            const foundNationality = nationalityOptions?.find(
              (opt) => {
                const optValue = opt.value?.toString().toLowerCase();
                const optLabel = opt.label?.toString().toLowerCase();
                const optCode = opt.code?.toString();
                const optId = opt.id?.toString();
                const searchString = nationalityString.toLowerCase();

                return (
                  optValue === searchString ||
                  optLabel === searchString ||
                  optCode === nationalityString ||
                  optId === nationalityString ||
                  optLabel?.includes("bahrain") && isBahraini ||
                  optValue?.includes("bahrain") && isBahraini
                );
              }
            );

            if (foundNationality) {
              // Use the actual value from the options (what SelectInput expects)
              nationalityCode = foundNationality.code || foundNationality.id || foundNationality.value;

              if (isBahraini) {
                console.log(`⚠️ Bahraini nationality detected from OCR - setting it but marking as invalid (code: ${nationalityCode})`);

                // Set error immediately for Bahraini
                setErrors((prevErrors) => ({
                  ...prevErrors,
                  nationality: translate(
                    "Helper nationality cannot be Bahraini.",
                    "جنسية العامل المنزلي لا يمكن أن تكون بحرينية."
                  ),
                }));
              } else {
                console.log(`✅ Converted nationality "${nationalityString}" to code: ${nationalityCode}`);
              }
            } else {
              // Couldn't find matching nationality - keep OCR value and let validation handle it
              nationalityCode = nationalityString;
              console.log(`⚠️ Couldn't find nationality code for: ${nationalityString}, using OCR value`);
            }
          }

          // CRITICAL: Only auto-fill fields that have valid non-empty values from OCR
          const updatedFormData = {
            ...prev,
            passportNumber: passportData.passportNumber?.trim() || prev.passportNumber,
            fullName: passportData.fullName?.trim() || prev.fullName,
            dateOfBirth: passportData.dateOfBirth?.day && passportData.dateOfBirth?.month && passportData.dateOfBirth?.year
              ? {
                  ...passportData.dateOfBirth,
                }
              : prev.dateOfBirth,
            gender: passportData.gender?.trim() || prev.gender,
            nationality: nationalityCode, // Use converted numeric code
          };

          // CRITICAL: Validate all auto-filled fields IMMEDIATELY (not in setTimeout)
          // Validate each field that was auto-filled
          if (passportData.passportNumber?.trim()) {
            validateField("passportNumber", updatedFormData.passportNumber, updatedFormData);
          }
          if (passportData.fullName?.trim()) {
            validateField("fullName", updatedFormData.fullName, updatedFormData);
          }
          if (passportData.gender?.trim()) {
            validateField("gender", updatedFormData.gender, updatedFormData);
          }
          if (passportData.dateOfBirth?.day && passportData.dateOfBirth?.month && passportData.dateOfBirth?.year) {
            const dateError = validateDateOfBirth(updatedFormData.dateOfBirth);
            if (dateError) {
              setErrors((prevErrors) => ({
                ...prevErrors,
                dateOfBirth: dateError,
              }));
            }
          }
          // Validate nationality if set
          if (nationalityCode) {
            validateField("nationality", nationalityCode, updatedFormData);
          }

          console.log("✅ OCR data validated immediately after auto-fill");

          return updatedFormData;
        });

        toast.success(
          translate(
            "Passport processed successfully! Form data has been auto-filled.",
            "تم معالجة جواز السفر بنجاح! تم ملء بيانات النموذج تلقائياً."
          )
        );
      } else {
        // Passport uploaded but OCR processing failed
        toast.error(
          translate(
            "Failed to process passport data. Please fill the form manually.",
            "فشل في معالجة بيانات جواز السفر. يرجى ملء النموذج يدوياً."
          )
        );
      }
    } else {
      // File removed
      setPassportFile(null);
      setFormData((prev) => ({
        ...prev,
        passportUploaded: false,
        passportProcessing: false,
        passportData: null,
        passportProcessed: false,
        passportFile: null,
      }));
    }
  };

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\u0600-\u06FF\s.]+$/.test(formData.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    } else if (formData.fullName.length < 2) {
      newErrors.fullName = translate(
        "Name must be at least 2 characters long.",
        "يجب أن يكون الاسم مكونًا من حرفين على الأقل."
      );
    }

    if (!formData.passportNumber.trim()) {
      newErrors.passportNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (formData.passportNumber.length < 6) {
      newErrors.passportNumber = translate(
        "Passport number must be at least 6 characters long.",
        "يجب أن يكون رقم جواز السفر مكونًا من 6 أحرف على الأقل."
      );
    }

    if (!formData.nationality) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      // Check if nationality is Bahraini (code 14 or string containing "bahrain")
      const isBahraini =
        formData.nationality === 14 || // Numeric code for BAHRAINI
        (typeof formData.nationality === "string" &&
          (formData.nationality.toLowerCase().includes("bahrain") ||
            formData.nationality.toLowerCase().includes("بحرين")));

      if (isBahraini) {
        newErrors.nationality = translate(
          "Helper nationality cannot be Bahraini",
          ".جنسية العامل المنزلي لا يمكن أن تكون بحرينية"
        );
      }
    }

    // Use the centralized date validation function
    if (
      !formData.dateOfBirth.day ||
      !formData.dateOfBirth.month ||
      !formData.dateOfBirth.year
    ) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      const dateError = validateDateOfBirth(formData.dateOfBirth);
      if (dateError) {
        newErrors.dateOfBirth = dateError;
      }
    }

    if (!formData.gender) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // CPR validation (required field, must be 9 digits)
    if (!formData.cprNumber || !formData.cprNumber.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^\d{9}$/.test(formData.cprNumber.replace(/\D/g, ""))) {
      newErrors.cprNumber = translate(
        "CPR must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
      );
    }

    // Passport upload validation
    if (!formData.passportUploaded) {
      newErrors.passport = translate(
        "Passport upload is required.",
        "تحميل جواز السفر مطلوب."
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContinue = () => {
    if (validateForm()) {
      try {
        // Save form data to Redux
        saveDomesticHelperDataInRedux(formData);
        handleNextStep();
      } catch (error) {
        console.error("Error saving helper details:", error);
      }
    }
  };

  // Mobile date picker handler
  const handleMobileDatePickerSelect = (dateString) => {
    if (!dateString) return;

    // Parse the date string (YYYY-MM-DD format from mobile date picker)
    const [year, month, day] = dateString.split("-");

    const updatedDateOfBirth = {
      day: day,
      month: month,
      year: year,
      fullDate: dateString,
    };

    setFormData((prev) => ({
      ...prev,
      dateOfBirth: updatedDateOfBirth,
    }));

    // Validate immediately after date selection
    const dateError = validateDateOfBirth(updatedDateOfBirth);
    setErrors((prev) => ({
      ...prev,
      dateOfBirth: dateError || "",
    }));
  };

  const isContinueDisabled = () => {
    // CPR is now required - must be 9 digits
    const hasCPR = formData.cprNumber && formData.cprNumber.replace(/\D/g, "").length === 9;

    return (
      !formData.fullName ||
      !formData.passportNumber ||
      !hasCPR || // CPR is required
      !formData.nationality ||
      !formData.dateOfBirth.day ||
      !formData.dateOfBirth.month ||
      !formData.dateOfBirth.year ||
      !formData.gender ||
      !formData.passportUploaded ||
      formData.passportProcessing ||
      isLoadingQuote ||
      !quoteData ||
      // An edit is waiting to be priced. Continuing here would take the
      // previous answers' premium and quote number into documents and payment.
      isQuoteStale
    );
  };

  const handleShowSummary = () => {
    setShowSummaryModal(true);
  };

  // Get premium from API only (no fallback calculation)
  // Quote data structure: { success: true, data: { totalPremium, premium, tax, ... }, raw: {...} }
  const totalPremium = quoteData?.data?.totalPremium
    ? parseFloat(quoteData.data.totalPremium)
    : 0;

  return (
    <div className="w-full about-your-helper">
      <DomesticHelperJourneyLayout
        is_hide_back_btn={false}
        customBackHandler={handlePrevSubstep}
      >
        <DomesticHelperBottomStickyBtnLayout
          btn_onClick={handleContinue}
          btn_disabled={isContinueDisabled()}
          isLoading={isLoadingQuote}
          btn_label={translate("Continue", "التالي")}
          addonComponent={
            // Shimmer covers the debounce as well as the request itself —
            // between an edit and its new price, the old premium is not this
            // helper's price and should not be sitting there looking like it.
            isLoadingQuote || (quoteData && isQuoteStale) ? (
              <div className="mr-10 max767:w-full max767:flex rtl:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-10 rtl:mr-0 items-center">
                <style>{`@keyframes policyShimmer { 100% { transform: translateX(100%); } }`}</style>
                <span
                  className="block h-7 w-28 max767:w-24 rounded-md relative overflow-hidden bg-[#E5E9EB] before:absolute before:inset-0 before:-translate-x-full before:animate-[policyShimmer_1.4s_infinite] before:bg-gradient-to-r before:from-transparent before:via-white/70 before:to-transparent"
                  aria-hidden="true"
                />
              </div>
            ) : quoteData ? (
              <div className="mr-10 max767:w-full max767:flex rtl:flex max767:mb-2 max767:justify-between ipad:mt-0 rtl:ml-10 rtl:mr-0">
                <button
                  onClick={handleShowSummary}
                  className="text-[#00384D] text-center text-base not-italic font-semibold underline mr-10 rtl:ml-10 rtl:mr-0"
                >
                  {translate("Show Summary", "عرض التسعيرة")}
                </button>
                {isRTL ? (
                  <strong className="text-[#1A2024] text-xl font-extrabold flex items-center gap-1 flex-row-reverse">
                    <span>{translate("BHD", "د.ب")}</span>
                    <span>
                      {formatPremiumWithCurrency(totalPremium, "", "en-US")}
                    </span>
                  </strong>
                ) : (
                  <strong className="text-[#1A2024] text-xl font-extrabold">
                    {formatPremiumWithCurrency(
                      totalPremium,
                      translate("BHD", "د.ب"),
                      "en-US"
                    )}
                  </strong>
                )}
              </div>
            ) : null
          }
        >
          <div className="w-full max767:w-full mx-auto px-6 pb-20">
            <div className="w-[600px] max767:w-full mx-auto">
              <TitleDescription
                title={translate("About Your Helper", "عن عاملك المنزلي")}
                description={translate(
                  "Please enter the required information about your helper.",
                  "يرجى إدخال المعلومات المطلوبة عن عاملك المنزلي."
                )}
              />

              <div className="space-y-6 mt-8">
                {/* Passport Upload Section */}
                <div className="passport-upload-section">
                  <h3 className="text-[#48535B] text-base mb-1">
                    {translate(
                      "Upload Domestic Helper Passport",
                      "تحميل جواز سفر العامل المنزلي"
                    )}
                  </h3>

                  <FileUpload
                    name="passport_helper"
                    toggleFileUpload={handlePassportUpload}
                    uploadedFile={passportFile}
                    key={passportFile?.fileName || "no-passport-file"}
                  />

                  {/* Passport Upload Error */}
                  {errors.passport && (
                    <p className="field-err text-red-500 text-sm mt-1 text-left rtl:text-right">
                      {errors.passport}
                    </p>
                  )}

                  {/* Passport Processing Status */}
                  {formData.passportProcessing && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center space-x-2 rtl:space-x-reverse">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <p className="text-sm text-blue-700">
                          {translate(
                            "Processing passport data...",
                            "جاري معالجة بيانات جواز السفر..."
                          )}
                        </p>
                      </div>
                      <p className="text-xs text-blue-600 mt-1">
                        {translate(
                          "Please wait while we extract your helper information...",
                          "يرجى الانتظار بينما نستخرج معلومات العاملة..."
                        )}
                      </p>
                    </div>
                  )}
                </div>

                {/* Full Name */}
                <Input
                  label={translate(
                    "Full Name (as it appears in the passport)",
                    "الاسم الكامل (كما هو مدرج في جواز السفر)"
                  )}
                  type="text"
                  value={formData.fullName}
                  handleChange={(e) =>
                    handleInputChange("fullName", e.target.value)
                  }
                  onBlur={() => validateField("fullName", formData.fullName)}
                  error={errors.fullName}
                  disabled={formData.passportProcessing || isLoadingQuote}
                />

                {/* Passport Number */}
                <Input
                  label={translate("Passport Number", "رقم جواز السفر")}
                  type="text"
                  value={formData.passportNumber}
                  handleChange={(e) =>
                    handleInputChange("passportNumber", e.target.value)
                  }
                  onBlur={() => validateField("passportNumber", formData.passportNumber)}
                  error={errors.passportNumber}
                  disabled={formData.passportProcessing || isLoadingQuote}
                />

                {/* CPR Number (Required) */}
                <Input
                  label={translate(
                    "CPR Number",
                    "رقم بطاقة الهوية (CPR)"
                  )}
                  type="text"
                  value={formData.cprNumber}
                  handleChange={(e) =>
                    handleInputChange("cprNumber", e.target.value)
                  }
                  onBlur={() => validateField("cprNumber", formData.cprNumber)}
                  error={errors.cprNumber}
                  maxLength={9}
                  disabled={isLoadingQuote}
                />

                {/* Domestic Application Number (Optional) */}
                <Input
                  label={translate(
                    "Domestic Application Number (if applicable)",
                    "رقم الطلب المحلي (إذا كان ذلك مناسبًا)"
                  )}
                  type="text"
                  value={formData.domesticApplicationNumber}
                  handleChange={(e) =>
                    handleInputChange(
                      "domesticApplicationNumber",
                      e.target.value
                    )
                  }
                  error={errors.domesticApplicationNumber}
                  disabled={isLoadingQuote}
                />

                {/* Nationality */}
                <div
                  className={`${
                    formData.passportProcessing || isLoadingQuote
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                >
                  <SelectInput
                    name="nationality"
                    formData={formData}
                    label={translate("Nationality", "الجنسية")}
                    options_data={nationalityOptions}
                    error={errors.nationality}
                    handleChange={(e) => {
                      if (!formData.passportProcessing && !isLoadingQuote) {
                        handleInputChange("nationality", e.target.value);
                        validateField("nationality", e.target.value);
                      }
                    }}
                    default_placeholder={translate(
                      "Select Nationality",
                      "اختر الجنسية"
                    )}
                  />
                </div>

                {/* Date of Birth */}
                <div className="date_of_birth formField">
                  <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
                    {translate("Date of Birth", "تاريخ الميلاد")}
                  </label>

                  {/* Mobile: native date picker (visible <=767px) */}
                  <div className="hidden max767:block">
                    <SingleDatePicker
                      value={formData.dateOfBirth.fullDate || ""}
                      onChange={handleMobileDatePickerSelect}
                      placeholder={translate("Select Date", "اختر التاريخ")}
                      error={errors.dateOfBirth}
                      useNativeMobile={true}
                      disabled={formData.passportProcessing || isLoadingQuote}
                    />
                  </div>
                  {/* Desktop: select dropdowns (visible >767px) */}
                  <div className="block max767:hidden">
                    <div className="flex gap-6 self-stretch">
                      <SelectInput
                        name="day"
                        options_data={daysOptions}
                        formData={{ day: formData.dateOfBirth.day || "" }}
                        default_placeholder={translate("Day", "يوم")}
                        error=""
                        handleChange={(e) =>
                          handleDateChange("day", e.target.value)
                        }
                        disabled={formData.passportProcessing || isLoadingQuote}
                        maxMenuHeight={185}
                      />

                      <SelectInput
                        name="month"
                        options_data={monthsOptions}
                        formData={{ month: formData.dateOfBirth.month || "" }}
                        default_placeholder={translate("Month", "شهر")}
                        error=""
                        handleChange={(e) =>
                          handleDateChange("month", e.target.value)
                        }
                        disabled={formData.passportProcessing || isLoadingQuote}
                        maxMenuHeight={185}
                      />

                      <SelectInput
                        name="year"
                        options_data={yearsOptions}
                        formData={{ year: formData.dateOfBirth.year || "" }}
                        default_placeholder={translate("Year", "سنة")}
                        error=""
                        handleChange={(e) =>
                          handleDateChange("year", e.target.value)
                        }
                        disabled={formData.passportProcessing || isLoadingQuote}
                        maxMenuHeight={185}
                      />
                    </div>
                    {errors.dateOfBirth && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.dateOfBirth}
                      </p>
                    )}
                  </div>
                </div>

                {/* Gender */}
                <div
                  className={`${
                    formData.passportProcessing || isLoadingQuote
                      ? "opacity-50 pointer-events-none"
                      : ""
                  }`}
                >
                  <FormTabs
                    label={translate("Gender", "الجنس")}
                    tabs={genderTabs}
                    activeTab={formData.gender}
                    onTabClick={(gender) =>
                      !formData.passportProcessing && handleGenderChange(gender)
                    }
                    error={errors.gender}
                  />
                </div>
              </div>
            </div>
          </div>
        </DomesticHelperBottomStickyBtnLayout>
      </DomesticHelperJourneyLayout>

      <DomesticHelperSummaryModal
        show={showSummaryModal}
        onClose={() => setShowSummaryModal(false)}
        helperData={formData}
        applicationData={applicationDetailsData}
        quoteData={quoteData}
      />
    </div>
  );
};

export default AboutYourHelper;
